import {
  Button,
  Flex,
  Icon,
  IconButton,
  Img,
  NumberInput,
  Text,
  Title,
  Tooltip,
} from '@wyz/ui';
import { useBoolean, useOnUpdate } from '@wyz/utils';
import { debounce, isNil, round } from 'lodash-es';
import { Card } from '@wyz/ui';
import {
  ICartMeta,
  IQuoteCart,
  IQuoteCartAction,
  IQuoteCartForm,
  IQuoteCartGroup,
  IQuoteCartService,
} from '~/purchase/types';

import { useState, type JSX } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { NumberField, PrivateComponent } from '~/common/components';
import { Trans, useConfig, useI18n, useLocale } from '~/common/context';
import { ROLES } from '~/common/helpers';
import { QuoteHelpers } from '~/purchase/helpers';
import { SERVICE_TYPE } from '~/purchase/schemas';
import { COUNTRY_ECOTAX_TYPE } from '@wyz/types';

function MountingService({
  group,
  service,
  actions,
}: {
  group: IQuoteCartGroup;
  service?: IQuoteCartService;
  actions: IQuoteCartAction;
}): JSX.Element {
  const { t } = useI18n();
  const [editing, editingHandlers] = useBoolean(false);
  const locale = useLocale();
  const [mountingPrice, setMountingPrice] = useState<number>(
    service ? round(service.priceTaxIncl, 2) : 0,
  );
  const defaultCurrencyCode = group.items.at(0)?.currency;
  useOnUpdate(() => {
    setMountingPrice(service ? round(service.priceTaxIncl, 2) : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service?.priceTaxIncl]);

  return (
    <Flex className='bg-neutral-grey-1 py-2 px-3' justifyContent='between'>
      <Flex className='gap-1' alignItems='center'>
        <Text heading='xs' weight='semi-bold' color='secondary'>
          <Trans i18nKey='app.common.quote.mounting' />
        </Text>
        {!editing && (
          <PrivateComponent roles={[ROLES.ROLE_QUOTE_CART_UPDATE_PRICE]}>
            <Icon
              id={'edit-mounting-price'}
              icon='pencil-line'
              size='xs'
              className='cursor-pointer'
              onClick={editingHandlers.setTrue}
            />
          </PrivateComponent>
        )}
      </Flex>
      {editing ? (
        <Flex className='w-50' alignItems='center'>
          <NumberInput
            name='mountingPrice'
            size='sm'
            withKnobs={false}
            defaultValue={mountingPrice}
            onChange={(value) => setMountingPrice(Number(value.target.value))}
          />
          <IconButton
            id='update-mounting-price'
            variant='tertiary-grey'
            icon='check-mark-line'
            iconSize='xs'
            isLoading={
              actions.updatePrice.isLoading || actions.addService.isLoading
            }
            onClick={() => {
              if (!isNil(mountingPrice)) {
                if (service) {
                  actions.updatePrice.process(
                    {
                      services: [{ id: service.id, price: mountingPrice }],
                    },
                    () => {
                      editingHandlers.setFalse();
                    },
                  );
                } else {
                  actions.addService.process(
                    {
                      serviceName: t('app.common.quote.mounting'),
                      serviceType: SERVICE_TYPE.MOUNTING,
                      priceTaxIncl: mountingPrice,
                      basePriceTaxIncl: mountingPrice,
                      targetEans: group.items.map((item) => item.tyre.ean),
                    },
                    () => {
                      editingHandlers.setFalse();
                    },
                  );
                }
              }
            }}
          />
          <IconButton
            id='cancel-update-mounting-price'
            variant='tertiary-grey'
            icon='close'
            iconSize='xs'
            onClick={editingHandlers.setFalse}
            disabled={
              actions.updatePrice.isLoading || actions.addService.isLoading
            }
          />
        </Flex>
      ) : (
        <Text
          id={'mounting-price'}
          heading='sm'
          weight='semi-bold'
          color='secondary'
        >
          {service
            ? service.priceTaxIncl === 0
              ? t('app.common.free_service')
              : locale.formatters.money(service.priceTaxIncl, {
                  currency: defaultCurrencyCode,
                })
            : '-'}
        </Text>
      )}
    </Flex>
  );
}

type Props = {
  cart: IQuoteCart;
  onNext: () => void;
  actions: IQuoteCartAction;
  privileges: ICartMeta;
};

function TotalSection({ cart, onNext, actions, privileges }: Props) {
  const locale = useLocale();

  const methods = useFormContext<IQuoteCartForm>();

  const watchedProducts = useWatch({
    name: 'products',
    control: methods.control,
  });
  const { cdnUrl, withCdnUrl } = useConfig();
  return (
    <Card>
      <Card.Body>
        <Title heading='lg' weight='bold' color='secondary' wrap>
          <Trans
            i18nKey={
              cart.groups.length > 1
                ? 'app.purchase.quote_cart_page.total_section.comparison_title'
                : 'app.purchase.quote_cart_page.total_section.title'
            }
          />
        </Title>
        <div className='px-1'>
          {cart.groups.map((group, index) => {
            const mountingService = group.services.find(
              (service) => service.serviceType === SERVICE_TYPE.MOUNTING,
            );

            return (
              <div key={index} className='my-2 border border-2 rounded'>
                {group.items.map((item, index) => (
                  <Flex
                    key={index}
                    justifyContent='between'
                    wrap='wrap'
                    className='bg-neutral-grey-1 p-2 flex-column flex-lg-row'
                  >
                    <Flex className='gap-1 flex-grow-1'>
                      <div className='flex-shrink-0'>
                        <Img
                          height={50}
                          src={
                            item.tyre.image
                              ? `/common/tyres${item.tyre.image}`
                              : undefined
                          }
                          defaultImage={withCdnUrl('/img/tire-sample.png')}
                          data-cy={`product-image-${item.tyre.ean}`}
                          baseUrl={cdnUrl}
                        />
                      </div>
                      <div>
                        <Text
                          id={`product-name-${item.tyre.ean}`}
                          heading='sm'
                          weight='bold'
                          color='tertiary-bold'
                        >
                          {item.tyre.profile}
                        </Text>
                        <Flex className='gap-1' alignItems='center'>
                          <Text
                            heading='xs'
                            weight='normal'
                            color='secondary-gray'
                          >
                            <Trans i18nKey='app.purchase.quote_cart_page.total_section.unit_price' />
                          </Text>
                          <Text
                            id={`product-unit-price-${item.tyre.ean}`}
                            heading='xs'
                            weight='semi-bold'
                            color='secondary-gray'
                          >
                            {locale.formatters.money(item.unitPriceTaxIncl, {
                              currency: item.currency,
                            })}
                          </Text>
                        </Flex>
                        <Flex className='gap-2' alignItems='center' wrap='wrap'>
                          <Text
                            heading='xs'
                            weight='normal'
                            color='secondary-gray'
                          >
                            <Trans i18nKey='app.purchase.quote_cart_page.total_section.quantity' />
                          </Text>
                          <Text
                            heading='xs'
                            weight='semi-bold'
                            color='secondary-gray'
                            id={`product-quantity-${item.tyre.ean}`}
                          >
                            {item.quantity}
                          </Text>
                          <Flex
                            alignItems='center'
                            className='gap-1'
                            style={{ maxWidth: '120px' }}
                          >
                            <NumberField
                              name={`products.${item.id}.quantity`}
                              size='sm'
                              min={QuoteHelpers.QUOTE_CART_MIN_QUANTITY}
                              max={QuoteHelpers.QUOTE_CART_MAX_QUANTITY}
                              onChangeValue={debounce((quantity) => {
                                if (
                                  !isNil(quantity) &&
                                  quantity >=
                                    QuoteHelpers.QUOTE_CART_MIN_QUANTITY &&
                                  quantity <=
                                    QuoteHelpers.QUOTE_CART_MAX_QUANTITY
                                ) {
                                  actions.updateItemQuantity.process(item.id, {
                                    quantity,
                                  });
                                }
                              }, 1000)}
                            />
                            {(watchedProducts[item.id].quantity <
                              QuoteHelpers.QUOTE_CART_MIN_QUANTITY ||
                              watchedProducts[item.id].quantity >
                                QuoteHelpers.QUOTE_CART_MAX_QUANTITY) && (
                              <Tooltip
                                label={
                                  <Trans i18nKey='app.purchase.quote_cart_page.invalid_quantity' />
                                }
                              >
                                <Icon
                                  icon='alert'
                                  className='text-danger-text'
                                />
                              </Tooltip>
                            )}
                          </Flex>
                        </Flex>
                      </div>
                    </Flex>
                    <Flex
                      direction='column'
                      alignItems='end'
                      className='mt-2 mt-lg-0'
                    >
                      <Text
                        id={`product-total-price-${item.tyre.ean}`}
                        heading='sm'
                        weight='semi-bold'
                        color='secondary'
                      >
                        {locale.formatters.money(item.totalTaxIncl, {
                          currency: item.currency,
                        })}
                      </Text>
                      {item.manualDiscount && !isNil(item.tyre.bf) && (
                        <Text
                          heading='xxs'
                          weight='semi-bold'
                          color='danger'
                          className='text-wrap'
                        >
                          <Trans
                            i18nKey='app.purchase.quote_cart_page.total_section.bf_discount'
                            values={{
                              value: QuoteHelpers.calculateBfDiscount(
                                item.tyre.bf,
                                QuoteHelpers.getPriceTaxExcl(
                                  item.unitPriceTaxIncl,
                                  cart.vatPercentage,
                                ),
                              ),
                            }}
                          />
                        </Text>
                      )}
                    </Flex>
                  </Flex>
                ))}
                {cart.items.length > 0 && (
                  <MountingService
                    group={group}
                    actions={actions}
                    service={mountingService}
                  />
                )}
                {group.services
                  .filter((service) => service.serviceType !== 'MOUNTING')
                  .map((service) => {
                    const price = locale.formatters.money(
                      service.priceTaxIncl,
                      {
                        currency: group.items.at(0)?.currency,
                      },
                    );
                    return (
                      <Flex
                        key={service.id}
                        className='bg-neutral-grey-1 py-2 px-3'
                        justifyContent='between'
                      >
                        <Text heading='xs' weight='semi-bold' color='secondary'>
                          {service.serviceName}
                        </Text>
                        <Text
                          id={`service-price-tax-included-group-${index}`}
                          heading='sm'
                          weight='semi-bold'
                          color={
                            service.manualDiscount ? 'warning' : 'secondary'
                          }
                        >
                          {service.manualDiscount &&
                          service.basePriceTaxIncl > 0 ? (
                            <Trans
                              i18nKey='app.purchase.quote_cart_page.price_edit.price_discount'
                              values={{
                                price,
                                discountPercentage: -service.discountPercentage,
                              }}
                            />
                          ) : (
                            price
                          )}
                        </Text>
                      </Flex>
                    );
                  })}
                <div className='bg-neutral-grey-2 py-2 px-3'>
                  <div className=' space-y-2'>
                    <Flex justifyContent='between' alignItems='center'>
                      <Text heading='xs' weight='semi-bold' color='secondary'>
                        <Trans i18nKey='app.purchase.quote_cart_page.total_section.sub_total' />
                      </Text>
                      <Text
                        id={`total-tax-excl-group-${index}`}
                        heading='xs'
                        weight='semi-bold'
                        color='secondary'
                      >
                        {locale.formatters.money(group.totalTaxExcl, {
                          currency: group.items.at(0)?.currency,
                        })}
                      </Text>
                    </Flex>
                    {privileges.ecotaxType !==
                      COUNTRY_ECOTAX_TYPE.NOT_DISPLAYED && (
                      <Flex justifyContent='between' alignItems='center'>
                        <Text heading='xs' weight='semi-bold' color='secondary'>
                          <Trans i18nKey='app.purchase.quote_cart_page.total_section.taxes' />
                        </Text>
                        <Text
                          id={`recycling-fee-group-${index}`}
                          heading='xs'
                          weight='semi-bold'
                          color='secondary'
                        >
                          {privileges.ecotaxType ===
                          COUNTRY_ECOTAX_TYPE.INCLUDED_IN_TYRE ? (
                            <Trans i18nKey='app.purchase.cart_page.ecotax_included' />
                          ) : (
                            locale.formatters.money(group.recyclingFee, {
                              currency: group.items.at(0)?.currency,
                            })
                          )}
                        </Text>
                      </Flex>
                    )}
                    <Flex justifyContent='between' alignItems='center'>
                      <Text heading='xs' weight='semi-bold' color='secondary'>
                        <Trans
                          i18nKey='app.purchase.quote_cart_page.total_section.vat'
                          values={{
                            value: cart.vatPercentage,
                          }}
                        />
                      </Text>
                      <Text
                        id={`vat-price-group-${index}`}
                        heading='xs'
                        weight='semi-bold'
                        color='secondary'
                      >
                        {locale.formatters.money(group.vatPrice, {
                          currency: group.items.at(0)?.currency,
                        })}
                      </Text>
                    </Flex>
                  </div>
                  <div className='border border-1 my-2'></div>
                  <Flex justifyContent='between' alignItems='center'>
                    <Text heading='xs' weight='bold' color='tertiary-bold'>
                      <Trans i18nKey='app.purchase.quote_cart_page.total_section.total' />
                    </Text>
                    <Text
                      id={`total-group-${index}`}
                      heading='normal'
                      weight='bold'
                      color='tertiary-bold'
                    >
                      {locale.formatters.money(group.totalTaxIncl, {
                        currency: group.items.at(0)?.currency,
                      })}
                    </Text>
                  </Flex>
                </div>
              </div>
            );
          })}
        </div>
        <Button
          id='next'
          variant='primary'
          onClick={onNext}
          fullWidth
          disabled={Object.values(watchedProducts).some(
            ({ quantity }) =>
              quantity < QuoteHelpers.QUOTE_CART_MIN_QUANTITY ||
              quantity > QuoteHelpers.QUOTE_CART_MAX_QUANTITY,
          )}
        >
          <Trans i18nKey='app.purchase.quote_cart_page.total_section.next' />
        </Button>
      </Card.Body>
    </Card>
  );
}

export default TotalSection;
